import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom, filter, tap, mergeMap } from 'rxjs/operators';
import { EFileType } from 'src/app/conf/user-files';
import { BoardUsersService } from 'src/app/services/board-users.service';
import { UserFileService } from 'src/app/services/user-file.service';
import { AppState } from '../app.reducer';
import * as UserFileActions from './user-file.actions';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import * as NotificationsActions from '../notifications/notifications.actions';

@Injectable()
export class UserFileEffects {
	getUserFiles$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.getUserFiles),
		concatMap(action => of(action).pipe(withLatestFrom(this.store.select('user', 'currentTechnician')))),
		switchMap(([action, currentTechnician]) => {
			let userId = action.userId;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			return this.userFileService.getUserFilesList(
				action.typeId,
				userId,
				action.siteId,
				action.noPagination,
				action.paging
			).pipe(
				map((resp) => {
					if (resp.Success) {
						return UserFileActions.getUserFilesSuccess({
							userFiles: resp.Data.Items,
							paging: resp.Data.Paging,
						});
					} else {
						return UserFileActions.getUserFilesFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => {
					console.error(error);
					return of(UserFileActions.getUserFilesFailed({ errorMessage: error.message }));
				})
			);
		})
	));

	getSiteImages$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.getSiteImages),
		concatMap(action => of(action).pipe(withLatestFrom(this.store.select('user', 'currentTechnician')))),
		switchMap(([action, currentTechnician]) => {
			let userId = null;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			return this.userFileService.getUserFilesList(
				EFileType.IMAGE,
				userId,
				null,
				true
			).pipe(
				map((resp) => {
					if (resp.Success) {
						return UserFileActions.getSiteImagesSuccess({ siteImages: resp.Data.Items });
					} else {
						return UserFileActions.getSiteImagesFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => {
					console.error(error);
					return of(UserFileActions.getSiteImagesFailed({ errorMessage: error.message }));
				})
			);
		})
	));

	addUserFile$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.addUserFile),
		concatMap(action => of(action).pipe(
			withLatestFrom(this.store.select('user', 'currentTechnician')),
		)),
		switchMap(([action, currentTechnician]) => {
			let userId = action.userId;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			const userFile = action.userFile;
			return this.userFileService.insertUserFile(userFile, userId)
				.pipe(
					map(resp => {
						if (!resp.Success) {
							return UserFileActions.addUserFileFailed({ errorMessage: resp.ErrorMessage });
						}
						if (!resp.Data) {
							return UserFileActions.addUserFileFailed({ errorMessage: this.translate.instant('GENERIC_ERROR') });
						}
						const uf = resp.Data;
						let message = this.translate.instant('FILE_SAVED_SUCCESS');
						switch (uf.Type) {
							case EFileType.BOARDUSERS_LIST:
								message = this.translate.instant('LIST_EXPORTED');
								break;
							case EFileType.IMAGE:
								uf.Content = null;
								message = this.translate.instant('IMAGE_UPLOADED_SUCCESS');
								break;
						}

						return UserFileActions.addUserFileSuccess({ userFile: uf, message });
					}),
					catchError(error => {
						console.error(error);
						return of(UserFileActions.addUserFileFailed({ errorMessage: error.message }));
					})
				);
		})
	));

	loadFileContent$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.getUserFileContent, UserFileActions.addUserFileSuccess),
		filter(action => !!action.userFile),
		switchMap((action) => {
			const userFile = action.userFile;
			const userId = action.userId;
			return this.userFileService.getUserFileContent(userFile.Id, userId)
				.pipe(
					map(userFileContent => {
						if (!userFileContent) {
							return UserFileActions.getUserFileContentFailed({ errorMessage: 'empty response' });
						}
						return UserFileActions.getUserFileContentSuccess({ userFile: action.userFile, fileContent: userFileContent });
					}),
					catchError(error => of(UserFileActions.getUserFileContentFailed({ errorMessage: error.message })))
				);
		})
	));

	deleteUserFile$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.deleteUserFile),
		concatMap(action => of(action).pipe(
			withLatestFrom(this.store.select('user', 'currentTechnician')),
		)),
		switchMap(([action, currentTechnician]) => {
			let userId = action.userId;
			if (currentTechnician) {
				userId = currentTechnician.PersonifiedId;
			}
			return this.userFileService.deleteUserFile(action.userFile.Id, userId)
				.pipe(
					map(response => {
						if (response.ErrorMessage) {
							return UserFileActions.deleteUserFileFailed({ errorMessage: response.ErrorMessage });
						} else {
							// this.messages.success('DELETE_FILE_SUCCESS');
							return UserFileActions.deleteUserFileSuccess({
								userFile: action.userFile,
								message: this.translate.instant('DELETE_FILE_SUCCESS'),
							});
						}
					}),
					catchError(error => {
						console.error(error);
						return of(UserFileActions.deleteUserFileFailed({ errorMessage: this.translate.instant('GENERIC_ERROR') }));
					})
				);
		})
	)
	);

	exportFinalUsersList$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.exportFinalUsersList),
		withLatestFrom(
			this.store.select('device', 'currentDevice'),
		),
		switchMap(([action, device]) => this.userFileService.exportFinalUsersList(device.Id, action.name, action.userId)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.exportFinalUsersListSuccess({ message: this.translate.instant('LIST_EXPORTED') });
					} else {
						return UserFileActions.exportFinalUsersListFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => {
					return of(UserFileActions.exportFinalUsersListFailed({ errorMessage: this.translate.instant('GENERIC_ERROR') }));
				})
			))
	));

	getFinalUsersLists$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.getFinalUsersLists),
		concatMap(action => of(action).pipe(
			withLatestFrom(this.store.select('device', 'currentDevice')),
		)),
		switchMap(([action, currentDevice]) => {
			let categoryId = currentDevice.CategoryId
			return this.userFileService.getFinalUsersLists(
				categoryId,
				false,
				action.paging,
				action.userId,
			).pipe(
				map((resp) => {
					if (resp.Success) {
						return UserFileActions.getFinalUsersListsSuccess({
							finalUsersLists: resp.Data.Items,
							paging: resp.Data.Paging,
						});
					} else {
						return UserFileActions.getFinalUsersListsFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => {
					console.error(error);
					return of(UserFileActions.getFinalUsersListsFailed({ errorMessage: error.message }));
				})
			);
		})
	));

	editFinalUsersList$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.editFinalUsersList),
		concatMap(action => of(action).pipe(
			// withLatestFrom(this.store.select('device', 'currentDevice')),
		)),
		switchMap((action) => {
			let list = action.list
			return this.userFileService.editFinalUsersList(
				list.Id,
				action.name,
				action.userId
			).pipe(
				map((resp) => {
					if (resp.Success) {
						return UserFileActions.editFinalUsersListSuccess({
							list: resp.Data,
							message: this.translate.instant('LIST_EDITED_SUCCESS')
						});
					} else {
						return UserFileActions.editFinalUsersListFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => {
					console.error(error);
					return of(UserFileActions.editFinalUsersListFailed({ errorMessage: error.message }));
				})
			);
		})
	));

	deleteFinalUsersList$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.deleteFinalUsersList),
		concatMap(action => of(action).pipe(
		)),
		switchMap((action) => {
			let exportId = action.exportList.Id;
			return this.userFileService.deleteFinalUsersList(exportId, action.userId)
				.pipe(
					map(resp => {
						if (resp.Success) {
							return UserFileActions.deleteFinalUsersListSuccess({
								finalUsersLists: action.exportList,
								message: this.translate.instant("LIST") + ' ' + action.exportList.Name + ' ' + this.translate.instant("IS_DELETED_FEMALE")
							});
						} else {
							return UserFileActions.deleteFinalUsersListFailed({ errorMessage: resp.ErrorMessage });
						}
					}),
					catchError(error => {
						console.error(error);
						return of(UserFileActions.deleteFinalUsersListFailed({ errorMessage: this.translate.instant('GENERIC_ERROR') }));
					})
				);
		})
	));

	importFinalUsersList$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.importFinalUsersList),
		withLatestFrom(
			this.store.select('device', 'currentDevice'),
		),
		switchMap(([action, device]) => this.userFileService.importFinalUsersList(device.Id, action.importList.Id, action.userId)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.importFinalUsersListSuccess({ message: action.importList.Name + ' ' + this.translate.instant('IS_IMPORTED_FEMALE') });
					} else {
						return UserFileActions.importFinalUsersListFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => {
					console.error(error);
					return of(UserFileActions.importFinalUsersListFailed({ errorMessage: this.translate.instant('GENERIC_ERROR') }));
				})
			)
		)
	));

	deleteFinalUsers$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.deleteFinalUsers),
		withLatestFrom(
			this.store.select('device', 'currentDevice'),
		),
		switchMap(([action, currentDevice]) => this.userFileService.deleteFinalUsers(currentDevice.Id, action.userId)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.deleteFinalUsersSuccess({
							message: this.translate.instant("FINAL_USERS_DELETE_SUCCESS")
						});
					} else {
						return UserFileActions.deleteFinalUsersFailed({ errorMessage: resp.ErrorMessage });
					}
				}),
				catchError(error => {
					console.error(error);
					return of(UserFileActions.deleteFinalUsersFailed({ errorMessage: this.translate.instant('GENERIC_ERROR') }));
				})
			)
		)
	));



	//-------------------------------EXPORT DEVICE

	//-GET LIST
	getExportDeviceList$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.getExportDeviceList),
		withLatestFrom(
			this.store.select('user', 'currentUser')),
		mergeMap(([action, user]) => this.userFileService.getExportDeviceList(user.Id, action.isImpersMode,
			false,
			action.paging)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.getExportDeviceListSuccess({
							exportLists: resp.Data.Items,
							paging: resp.Data.Paging,
						});
					} else {
						return UserFileActions.getExportDeviceListFailed({ errorMessage: this.translate.instant('GET_LIST_EXPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.getExportDeviceListFailed({ errorMessage: this.translate.instant('GET_LIST_EXPORT_DEVICE_FAILED') })))
			)
		)
	));

	//-CREATE
	exportDevice$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.exportDevice),
		withLatestFrom(this.store.select('device', 'currentDevice')),
		mergeMap(([action, device]) => this.userFileService.exportDevice(device.Id, action.exportInfo)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.exportDeviceSuccess({ message: this.translate.instant('EXPORT_DEVICE_SUCCESS') });
					} else {
						return UserFileActions.exportDeviceFailed({ errorMessage: this.translate.instant('EXPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.exportDeviceFailed({ errorMessage: this.translate.instant('EXPORT_DEVICE_FAILED') })))
			)
		)
	));


	//-UPDATE
	updateExportDevice$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.updateExportDevice),
		mergeMap((action) => this.userFileService.updateExportDevice(action.exportId, action.exportInfo)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.updateExportDeviceSuccess({ message: this.translate.instant('UPDATE_EXPORT_DEVICE_SUCCESS') });
					} else {
						return UserFileActions.updateExportDeviceFailed({ errorMessage: this.translate.instant('UPDATE_EXPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.updateExportDeviceFailed({ errorMessage: this.translate.instant('UPDATE_EXPORT_DEVICE_FAILED') })))
			)
		)
	));


	//-DELETE
	deleteExportDevice$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.deleteExportDevice),
		mergeMap((action) => this.userFileService.deleteExportDevice(action.exportInfo.Id)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.deleteExportDeviceSuccess({ message: this.translate.instant('DELETE_EXPORT_DEVICE_SUCCESS') });
					} else {
						return UserFileActions.deleteExportDeviceFailed({ errorMessage: this.translate.instant('DELETE_EXPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.deleteExportDeviceFailed({ errorMessage: this.translate.instant('DELETE_EXPORT_DEVICE_FAILED') })))
			)
		)
	));


	//-IMPORT
	importDevice$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.importDevice),
		withLatestFrom(this.store.select('device', 'currentDevice')),
		mergeMap(([action, device]) => this.userFileService.importDevice(device.Id, action.exportId, action.importInfo)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.importDeviceSuccess({ message: this.translate.instant('IMPORT_DEVICE_SUCCESS') });
					} else {
						return UserFileActions.importDeviceFailed({ errorMessage: this.translate.instant(resp.ErrorMessage) });
					}
				}),
				catchError(error => of(UserFileActions.importDeviceFailed({ errorMessage: this.translate.instant('IMPORT_DEVICE_FAILED') })))
			)
		)
	));
	//-------------------------------EXPORT DEVICE

	//-------------------------------EXPORT DEVICE MTMA

	//-GET LIST
	getMtmaExportDeviceList$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.getMtmaExportDeviceList),
		withLatestFrom(
			this.store.select('user', 'currentUser')),
		mergeMap(([action, user]) => this.userFileService.getMtmaExportDeviceList(user.Id, action.isImpersMode,
			false,
			action.paging)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.getMtmaExportDeviceListSuccess({
							exportLists: resp.Data.Items,
							paging: resp.Data.Paging,
						});
					} else {
						return UserFileActions.getMtmaExportDeviceListFailed({ errorMessage: this.translate.instant('GET_LIST_EXPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.getMtmaExportDeviceListFailed({ errorMessage: this.translate.instant('GET_LIST_EXPORT_DEVICE_FAILED') })))
			)
		)
	));

	//-CREATE
	exportMtmaDevice$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.exportMtmaDevice),
		withLatestFrom(this.store.select('device', 'currentDevice'),
			this.store.select('user', 'currentUser')),
		mergeMap(([action, device, user]) => this.userFileService.exportMtmaDevice(device.Id, user.Id, action.exportInfo, action.isImpersMode)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.exportMtmaDeviceSuccess({ message: this.translate.instant('EXPORT_MTMA_DEVICE_SUCCESS') });
					} else {
						return UserFileActions.exportMtmaDeviceFailed({ errorMessage: this.translate.instant('EXPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.exportMtmaDeviceFailed({ errorMessage: this.translate.instant('EXPORT_DEVICE_FAILED') })))
			)
		)
	));


	//-UPDATE
	updateMtmaExportDevice$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.updateMtmaExportDevice),
		withLatestFrom(
			this.store.select('user', 'currentUser')),
		mergeMap(([action, user]) => this.userFileService.updateMtmaExportDevice(action.exportId, action.exportInfo, user.Id, action.isImpersMode)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.updateMtmaExportDeviceSuccess({ message: this.translate.instant('UPDATE_EXPORT_DEVICE_SUCCESS') });
					} else {
						return UserFileActions.updateMtmaExportDeviceFailed({ errorMessage: this.translate.instant('UPDATE_EXPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.updateMtmaExportDeviceFailed({ errorMessage: this.translate.instant('UPDATE_EXPORT_DEVICE_FAILED') })))
			)
		)
	));


	//-DELETE
	deleteMtmaExportDevice$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.deleteMtmaExportDevice),
		withLatestFrom(
			this.store.select('user', 'currentUser')),
		mergeMap(([action, user]) => this.userFileService.deleteMtmaExportDevice(action.exportInfo.Id, user.Id, action.isImpersMode)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.deleteMtmaExportDeviceSuccess({ message: this.translate.instant('DELETE_EXPORT_DEVICE_SUCCESS') });
					} else {
						return UserFileActions.deleteMtmaExportDeviceFailed({ errorMessage: this.translate.instant('DELETE_EXPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.deleteMtmaExportDeviceFailed({ errorMessage: this.translate.instant('DELETE_EXPORT_DEVICE_FAILED') })))
			)
		)
	));


	//-IMPORT
	importMtmaDevice$ = createEffect(() => this.actions$.pipe(
		ofType(UserFileActions.importMtmaDevice),
		withLatestFrom(this.store.select('device', 'currentDevice'),
			this.store.select('user', 'currentUser')),
		mergeMap(([action, device, user]) => this.userFileService.importMtmaDevice(device.Id, action.exportId, action.importInfo, user.Id, action.isImpersMode)
			.pipe(
				map(resp => {
					if (resp.Success) {
						return UserFileActions.importMtmaDeviceSuccess({ message: this.translate.instant('IMPORT_MTMA_DEVICE_SUCCESS') });
					} else {
						return UserFileActions.importMtmaDeviceFailed({ errorMessage: this.translate.instant('IMPORT_DEVICE_FAILED') });
					}
				}),
				catchError(error => of(UserFileActions.importMtmaDeviceFailed({ errorMessage: this.translate.instant('IMPORT_DEVICE_FAILED') })))
			)
		)
	));
	//-------------------------------EXPORT DEVICE


	notifySuccess$ = createEffect(() => this.actions$.pipe(
		ofType(
			UserFileActions.addUserFileSuccess,
			UserFileActions.deleteUserFileSuccess,
			UserFileActions.exportFinalUsersListSuccess,
			UserFileActions.deleteFinalUsersListSuccess,
			UserFileActions.editFinalUsersListSuccess,
			UserFileActions.importFinalUsersListSuccess,
			UserFileActions.deleteFinalUsersSuccess,
			UserFileActions.exportDeviceSuccess,
			UserFileActions.deleteExportDeviceSuccess,
			UserFileActions.updateExportDeviceSuccess,
			UserFileActions.importDeviceSuccess,
			UserFileActions.exportMtmaDeviceSuccess,
			UserFileActions.deleteMtmaExportDeviceSuccess,
			UserFileActions.updateMtmaExportDeviceSuccess,
			UserFileActions.importMtmaDeviceSuccess,
		),
		tap((action) => {
			if (action.message === '') {
				return;
			}
			const notification = new Notification("USER-FILE", action.message, NotificationLevel.Success, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		})
	), { dispatch: false });

	notifyError$ = createEffect(() => this.actions$.pipe(
		ofType(
			UserFileActions.addUserFileFailed,
			UserFileActions.exportFinalUsersListFailed,
			UserFileActions.deleteFinalUsersListFailed,
			UserFileActions.editFinalUsersListFailed,
			UserFileActions.importFinalUsersListFailed,
			UserFileActions.deleteFinalUsersFailed,
			UserFileActions.exportDeviceFailed,
			UserFileActions.deleteExportDeviceFailed,
			UserFileActions.updateExportDeviceFailed,
			UserFileActions.getExportDeviceListFailed,
			UserFileActions.importDeviceFailed,
			UserFileActions.exportMtmaDeviceFailed,
			UserFileActions.deleteMtmaExportDeviceFailed,
			UserFileActions.updateMtmaExportDeviceFailed,
			UserFileActions.getMtmaExportDeviceListFailed,
			UserFileActions.importMtmaDeviceFailed,
		),
		tap((action) => {
			if (action.errorMessage === '') {
				return;
			}
			const notification = new Notification("USER-FILE", action.errorMessage, NotificationLevel.Error, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		})
	), { dispatch: false });

	constructor(
		private actions$: Actions,
		private store: Store<AppState>,
		private userFileService: UserFileService,
		private boardUsersService: BoardUsersService,
		private translate: TranslateService,
	) {
	}
}
